import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { ArrowLeft, ArrowRight, Check, LoaderCircle, PartyPopper, RefreshCcw, Sparkles, X } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { useTutorial } from "./TutorialContext";
import { walkthroughSteps } from "./tutorialCopy";
import { findTutorialTarget } from "./tutorialTargets";

const PAD = 8;

function targetRect(node) {
  if (!node) return null;
  const rect = node.getBoundingClientRect();
  if (!rect.width || !rect.height) return null;
  const left = Math.max(0, rect.left - PAD);
  const right = Math.min(window.innerWidth, rect.right + PAD);
  const top = Math.max(0, rect.top - PAD);
  const bottom = Math.min(window.innerHeight, rect.bottom + PAD);
  const width = Math.max(0, right - left);
  const height = Math.max(0, bottom - top);
  if (!width || !height) return null;
  return {
    bottom,
    height,
    left,
    right,
    top,
    width,
  };
}

export function GuidedTutorialOverlay() {
  const tutorial = useTutorial();
  const step = walkthroughSteps[tutorial.stage];
  const [measurement, setMeasurement] = useState(null);
  const [measureRequest, setMeasureRequest] = useState(0);
  const [, setRevision] = useState(0);
  const rect = measurement?.stage === tutorial.stage ? measurement.rect : null;

  useEffect(() => {
    if (!tutorial.active || !step) return undefined;
    let frame;
    let observedTarget = null;
    let hasScrolled = false;
    const targetObserver = typeof ResizeObserver === "undefined" ? null : new ResizeObserver(() => schedule());
    const update = () => {
      const node = findTutorialTarget(step.selector);
      if (node !== observedTarget) {
        targetObserver?.disconnect();
        observedTarget = node;
        if (node) targetObserver?.observe(node);
      }
      if (node && !hasScrolled) {
        hasScrolled = true;
        node.scrollIntoView({ block: "center", inline: "nearest", behavior: "smooth" });
      }
      setMeasurement({ stage: tutorial.stage, rect: targetRect(node) });
    };
    const schedule = () => {
      window.cancelAnimationFrame(frame);
      frame = window.requestAnimationFrame(update);
    };
    update();
    const observer = new MutationObserver(schedule);
    observer.observe(document.body, {
      attributeFilter: ["class", "disabled", "hidden", "style"],
      attributes: true,
      childList: true,
      subtree: true,
    });
    window.addEventListener("resize", schedule);
    window.addEventListener("scroll", schedule, true);
    document.addEventListener("input", schedule, true);
    document.addEventListener("change", schedule, true);
    return () => {
      observer.disconnect();
      targetObserver?.disconnect();
      window.cancelAnimationFrame(frame);
      window.removeEventListener("resize", schedule);
      window.removeEventListener("scroll", schedule, true);
      document.removeEventListener("input", schedule, true);
      document.removeEventListener("change", schedule, true);
    };
  }, [measureRequest, step, tutorial.active, tutorial.stage]);

  const canGoBack = ["price", "existing", "add", "cart", "payment", "customer", "phone", "due", "amount", "complete", "cost", "stock", "saveProduct"].includes(tutorial.stage);
  const enabled = (() => {
    if (!rect) return false;
    const element = findTutorialTarget(step?.selector);
    if (!element) return false;
    if (tutorial.stage === "search" || tutorial.stage === "price" || tutorial.stage === "customer") return String(element.value || "").trim().length > 0;
    if (["add", "complete", "saveProduct"].includes(tutorial.stage)) return !element.disabled;
    return true;
  })();

  const previous = () => {
    const previousStage = {
      existing: "search", price: "search", add: "price", cart: "search", payment: "cart",
      customer: "payment", phone: "customer", due: "phone", amount: "due", complete: "payment",
      cost: "inventoryRow", stock: "cost", saveProduct: "stock",
    }[tutorial.stage];
    if (previousStage) tutorial.setStage(previousStage);
  };

  const next = () => {
    const element = findTutorialTarget(step.selector);
    if (!element || !enabled) return;
    if (tutorial.stage === "search") {
      tutorial.setStage(findTutorialTarget('[data-testid^="sell-product-result-"][tabindex="0"]') ? "existing" : "price");
      return;
    }
    if (tutorial.stage === "existing") {
      element.click();
      return;
    }
    if (tutorial.stage === "price") return tutorial.setStage("add");
    if (tutorial.stage === "add") {
      element.click();
      return;
    }
    if (tutorial.stage === "cart") return tutorial.setStage("payment");
    if (tutorial.stage === "payment") {
      const credit = document.querySelector('[data-testid="sell-payment-credit"]')?.getAttribute("data-state") === "active";
      return tutorial.setStage(credit ? "customer" : "complete");
    }
    if (tutorial.stage === "customer") return tutorial.setStage("phone");
    if (tutorial.stage === "phone") return tutorial.setStage("due");
    if (tutorial.stage === "due") return tutorial.setStage("amount");
    if (tutorial.stage === "amount") return tutorial.setStage("complete");
    if (tutorial.stage === "complete" || tutorial.stage === "saveProduct") return element.click();
    if (tutorial.stage === "inventoryRow") return element.click();
    if (tutorial.stage === "cost") return tutorial.setStage("stock");
    if (tutorial.stage === "stock") return tutorial.setStage("saveProduct");
    if (tutorial.stage === "history") return tutorial.advanceHistory();
    if (tutorial.stage === "dashboard") return tutorial.acknowledgeDashboard();
  };

  useEffect(() => {
    const update = () => setRevision((value) => value + 1);
    document.addEventListener("input", update, true);
    document.addEventListener("change", update, true);
    return () => {
      document.removeEventListener("input", update, true);
      document.removeEventListener("change", update, true);
    };
  }, []);

  const overlay = tutorial.active && !tutorial.completionOpen && step ? (
    rect ? (
      <div className="pointer-events-none fixed inset-0 z-[90]" aria-label="Guided walkthrough" role="dialog" aria-modal="true">
        <Block style={{ left: 0, right: 0, top: 0, height: rect.top }} />
        <Block style={{ left: 0, top: rect.top, width: rect.left, height: rect.height }} />
        <Block style={{ left: rect.right, right: 0, top: rect.top, height: rect.height }} />
        <Block style={{ left: 0, right: 0, top: rect.bottom, bottom: 0 }} />
        <div className="pointer-events-none fixed rounded-2xl border-2 border-cyan-300 bg-white/5 shadow-[0_0_0_3px_rgba(13,148,136,0.32),0_18px_55px_rgba(0,0,0,0.28)]" style={{ left: rect.left, top: rect.top, width: rect.width, height: rect.height }} />
        <CoachCard rect={rect} step={step} canGoBack={canGoBack} enabled={enabled} onBack={previous} onNext={next} onSkip={tutorial.skip} />
      </div>
    ) : (
      <WaitingOverlay
        canGoBack={canGoBack}
        onBack={previous}
        onRetry={() => setMeasureRequest((value) => value + 1)}
        onSkip={tutorial.skip}
        step={step}
      />
    )
  ) : null;

  return createPortal(
    <>
      {overlay}
      <CompletionDialog open={tutorial.completionOpen} onFinish={tutorial.finish} />
    </>,
    document.body,
  );
}

function Block({ style }) {
  return <div className="pointer-events-auto fixed bg-slate-950/65 backdrop-blur-[1px]" style={style} />;
}

function CoachCard({ canGoBack, enabled, onBack, onNext, onSkip, rect, step }) {
  const cardWidth = Math.min(420, window.innerWidth - 24);
  const left = Math.max(12, Math.min(window.innerWidth - cardWidth - 12, rect.left));
  const fitsBelow = rect.bottom + 190 < window.innerHeight;
  const top = fitsBelow ? rect.bottom + 12 : Math.max(12, rect.top - 178);
  return (
    <section aria-live="polite" className="pointer-events-auto fixed overflow-hidden rounded-2xl border border-white/15 bg-[#075a5b] p-4 text-left text-white shadow-2xl" style={{ left, top, width: cardWidth }}>
      <div className="flex items-start gap-3">
        <span className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-white/10"><Sparkles className="h-4 w-4" /></span>
        <div className="min-w-0 flex-1">
          <div className="flex items-start justify-between gap-3">
            <div className="pt-1 text-sm font-semibold">{step.title}</div>
            <Button variant="ghost" size="sm" onClick={onSkip} className="h-8 shrink-0 gap-1.5 px-2 text-xs text-white/75 hover:bg-white/10 hover:text-white">
              Skip guide <X className="h-3.5 w-3.5" />
            </Button>
          </div>
          <p className="mt-1 text-xs leading-5 text-white/75">{step.body}</p>
        </div>
      </div>
      <div className="mt-4 flex items-center justify-between gap-3">
        <span className="text-[11px] font-semibold tracking-wider text-white/60">{step.count}</span>
        <div className="flex gap-2">
          {canGoBack ? <Button variant="ghost" size="sm" onClick={onBack} className="h-9 gap-1.5 text-white hover:bg-white/10 hover:text-white"><ArrowLeft className="h-3.5 w-3.5" /> Previous</Button> : null}
          <Button size="sm" disabled={!enabled} onClick={onNext} className="h-9 gap-1.5 bg-white text-[#075a5b] hover:bg-white/90">Next <ArrowRight className="h-3.5 w-3.5" /></Button>
        </div>
      </div>
    </section>
  );
}

function WaitingOverlay({ canGoBack, onBack, onRetry, onSkip, step }) {
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/65 p-4 backdrop-blur-[1px]" aria-label="Preparing guided walkthrough step" role="dialog" aria-modal="true">
      <section aria-live="polite" className="w-full max-w-sm overflow-hidden rounded-2xl border border-white/15 bg-[#075a5b] p-5 text-white shadow-2xl">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-white/10">
            <LoaderCircle className="h-4 w-4 animate-spin" />
          </span>
          <div className="min-w-0 flex-1">
            <div className="flex items-start justify-between gap-3">
              <div className="pt-1 text-sm font-semibold">Preparing {step.title.toLowerCase()}</div>
              <Button variant="ghost" size="sm" onClick={onSkip} className="h-8 shrink-0 gap-1.5 px-2 text-xs text-white/75 hover:bg-white/10 hover:text-white">
                Skip guide <X className="h-3.5 w-3.5" />
              </Button>
            </div>
            <p className="mt-1 text-xs leading-5 text-white/75">This screen is still loading. The guide will highlight the right control as soon as it is ready.</p>
          </div>
        </div>
        <div className="mt-4 flex items-center justify-end gap-2">
          {canGoBack ? <Button variant="ghost" size="sm" onClick={onBack} className="h-9 gap-1.5 text-white hover:bg-white/10 hover:text-white"><ArrowLeft className="h-3.5 w-3.5" /> Previous</Button> : null}
          <Button size="sm" onClick={onRetry} className="h-9 gap-1.5 bg-white text-[#075a5b] hover:bg-white/90"><RefreshCcw className="h-3.5 w-3.5" /> Retry</Button>
        </div>
      </section>
    </div>
  );
}

function CompletionDialog({ open, onFinish }) {
  return (
    <Dialog open={open}>
      <DialogContent onEscapeKeyDown={(event) => event.preventDefault()} onPointerDownOutside={(event) => event.preventDefault()} className="max-w-md overflow-hidden border-primary/10 p-0 shadow-2xl [&>button]:hidden">
        <div className="bg-gradient-to-br from-[#064e4f] via-[#075a5b] to-[#0b7270] px-6 pb-7 pt-8 text-white">
          <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-white/12 ring-1 ring-white/15"><PartyPopper className="h-5 w-5" /></span>
          <DialogHeader className="mt-5 text-left">
            <p className="text-[10px] font-semibold uppercase tracking-[0.24em] text-white/60">First milestone complete</p>
            <DialogTitle className="mt-2 text-2xl text-white">Your shop is ready to learn from every sale</DialogTitle>
            <DialogDescription className="mt-2 leading-6 text-white/70">You recorded your first sale, completed product setup, found the transaction, and saw how it shapes your dashboard.</DialogDescription>
          </DialogHeader>
        </div>
        <div className="space-y-3 px-6 py-5">
          {["Stock levels stay dependable", "Profit insights become more accurate", "Every transaction remains easy to review"].map((benefit) => <div key={benefit} className="flex items-center gap-3 text-sm text-muted-foreground"><span className="flex h-7 w-7 items-center justify-center rounded-full bg-primary/10 text-primary"><Check className="h-3.5 w-3.5" /></span>{benefit}</div>)}
        </div>
        <DialogFooter className="grid grid-cols-1 gap-2 border-t bg-muted/20 px-6 py-5 sm:grid-cols-2">
          <Button variant="outline" onClick={() => onFinish(true)}>Explore tutorials</Button>
          <Button onClick={() => onFinish(false)}>Continue to dashboard</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
